import { graphql } from "gatsby";
import React, { useEffect } from "react";

import Primary from "~/components/layout/Primary";
import Seo from "~/components/Seo";

import { PageArticleSidebar, PageBlockList, PageHeader } from "./components";

import { useFormContext } from "~/context/FormContext";
import usePageBreadcrumbs from "./hooks/usePageBreadcrumbs";

import ContentContainer from "~/components/layout/ContentContainer";
import TwoColumnLayout from "~/components/layout/TwoColumnLayout";

const MainContent = ({ templateName, breadcrumbs, data, urlPermutations }) => {
    if (templateName === "WithSidebar") {
        return (
            <>
                <PageHeader
                    data={data}
                    breadcrumbs={breadcrumbs}
                    urlPermutations={urlPermutations}
                />
                <ContentContainer>
                    <TwoColumnLayout
                        stickySidebar
                        main={
                            <PageBlockList
                                data={data}
                                breadcrumbs={breadcrumbs}
                                urlPermutations={urlPermutations}
                            />
                        }
                        sidebar={<PageArticleSidebar data={data} />}
                    />
                </ContentContainer>
            </>
        );
    }

    return (
        <PageBlockList
            data={data}
            breadcrumbs={breadcrumbs}
            urlPermutations={urlPermutations}
        />
    );
};

const Page = ({ data, location, pageContext }) => {
    const {
        wpPage: {
            title,
            languageCode,
            template: { templateName },
        },
    } = data;

    const { setHiddenFields } = useFormContext();

    useEffect(() => {
        setHiddenFields({
            pageTitle: title,
            pageUrl: location.href,
        });
    }, [setHiddenFields, location.href, title]);

    const breadcrumbs = usePageBreadcrumbs(data);
    const urlPermutations = pageContext.urlPermutations;

    return (
        <Primary {...{ urlPermutations, pageCode: languageCode }}>
            <MainContent
                templateName={templateName}
                breadcrumbs={breadcrumbs}
                data={data}
                urlPermutations={urlPermutations}
            />
        </Primary>
    );
};

export default Page;

export const Head = ({ data, location, pageContext }) => {
    const { title, seo, featuredImage, language } = data.wpPage;
    const urlPermutations = pageContext.urlPermutations;

    return (
        <Seo
            {...{
                seo,
                featuredImage,
                location,
                urlPermutations,
                pageTitle: title,
            }}
            languageCode={language?.code}
        />
    );
};

export const query = graphql`
    query PageQuery($id: String!) {
        wpPage(id: { eq: $id }) {
            id
            slug
            title
            languageCode
            language {
                code
            }
            seo {
                ...SeoPostFragment
            }
            template {
                templateName
            }
            uri
            pageheaderOptions {
                copy
                moreText
                noMarginBottom
                subtitle
                title
                links {
                    action
                    linkLabel
                    linkUrl
                    formId
                    isEmbed
                    video {
                        ...VideoFragment
                    }
                    videoUrl
                }
                hideTwitter
            }
            ctaSections {
                sections {
                    title
                    links {
                        linkLabel
                        linkUrl
                        icon
                        type
                        formId
                        doc {
                            ...DocumentFragment
                        }
                    }
                }
            }
            featuredImage {
                node {
                    sourceUrl
                }
            }
            ancestors {
                nodes {
                    ... on WpPage {
                        title
                        slug
                        uri
                        languageCode
                    }
                }
            }
            translations {
                title
                slug
                languageCode
                uri
            }
            blocks {
                ...AcfQuoteBlockFragment
                ...AccordionsBlockFragment
                ...AdFullBlockFragment
                ...AdWithDropdownBlockFragment
                ...AlertBlockFragment
                ...ButtonsBlockFragment
                ...CardLinksBlockFragment
                ...CodeBlockFragment
                ...ColumnsBlockFragment
                ...ContactBlockFragment
                ...ContentBlockFragment
                ...CookieTypesBlockFragment
                ...CrossReferenceSearchBlockFragment
                ...CtaSectionsBlockFragment
                ...DocumentBlockFragment
                ...DocumentLibraryBlockFragment
                ...EmailVerificationBlockFragment
                ...EmbedBlockFragment
                ...EventsBlockFragment
                ...FeaturedKcLeadershipBlockFragment
                ...FeaturedNewsBlockFragment
                ...FeaturedProductsCarouselBlockFragment
                ...FeaturedResourcesBlockFragment
                ...FileBlockFragment
                ...FormBlockFragment
                ...FreeformBlockFragment
                ...FullWidthCtaBlockFragment
                ...GraphicWithHotspotsBlockFragment
                ...GroupBlockFragment
                ...GroupOfLinksBlockFragment
                ...HeadingBlockFragment
                ...HeroBlockFragment
                ...HeroSliderBlockFragment
                ...HtmlBlockFragment
                ...ImageBlockFragment
                ...ImageSliderBlockFragment
                ...InfoCardsBlockFragment
                ...IntroductionBlockFragment
                ...JsEmbedBlockFragment
                ...JumpLinksBlockFragment
                ...KnowledgeCenterBlockFragment
                ...KnowledgeCenterFilterBlockFragment
                ...LatestNewsBlockFragment
                ...LightboxImageBlockFragment
                ...LinkSectionBlockFragment
                ...ListBlockFragment
                ...LottieBlockFragment
                ...MathjaxBlockFragment
                ...MaterialsPartsBuilderBlockFragment
                ...MediaTextBlockFragment
                ...NewsBlockFragment
                ...NewsCardCarouselBlockFragment
                ...PageHeaderBlockFragment
                ...PageHeader404BlockFragment
                ...PartnersBlockFragment
                ...ParagraphBlockFragment
                ...PastEventsBlockFragment
                ...ProductTableBlockFragment
                ...ProductCardSectionBlockFragment
                ...PullquoteBlockFragment
                ...QuoteBlockFragment
                ...ReferenceDesignCardsBlockFragment
                ...ReferenceDesignsTableBlockFragment
                ...ResourcesBlockFragment
                ...ShoppingCartBlockFragment
                ...SearchBlockFragment
                ...SeparatorBlockFragment
                ...SidebarTabsBlockFragment
                ...SidebarTitleSectionBlockFragment
                ...SpacerBlockFragment
                ...SpeedfitBlockFragment
                ...StatCardsBlockFragment
                ...TabbedAdBlockFragment
                ...TableBlockFragment
                ...TabSectionBlockFragment
                ...TestimonialsBlockFragment
                ...ThreeColumnCardsBlockFragment
                ...TimelineBlockFragment
                ...TwitterTimelineBlockFragment
                ...UpcomingEventsBlockFragment
                ...VideoBlockFragment
                ...VideoAdBlockFragment
                ...VideoEmbedBlockFragment
                ...VideoPlaylistBlockFragment
                ...WhereToBuyBlockFragment
            }
        }
    }
`;
